import { useState, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import PageContainer from '../../shared/pageContainer';
import AbsenceSets from '../../shared/absenceSets';
import CustomButton from '../../shared/customButton';
import CustomFormDrawer from '../../shared/customFormDrawer';
import AlertDialog from '../../shared/alertDialog';
import NotificationCard from '../../shared/notificationCard';
import {
  API_USERS_BASIC,
  API_ABSENCE_SETS,
  API_ABSENCE,
  API_ABSENCE_EXTENSIONS,
  API_HOLIDAYS,
  api_holiday,
  api_absence_set,
  api_absence_set_duplicate,
  api_absence_set_users,
  api_absence,
  api_absence_users,
  api_absence_extension,
  api_absence_extension_users,
  api_absence_set_and_absence_add_users,
  api_absence_report_template,
} from '../../../constants/apiRoutes';
import { USER_STATUSES } from '../../../constants/statuses';
import { SYSTEM_ROLES, SYSTEM_ROLE_OPTIONS } from '../../../constants/roles';
import { APP_PAGES, PARAMS } from '../../../constants/pages';
import { ABSENCE_FIELD_NAMES, ABSENCE_ICONS } from '../../../constants/absence';
import { PEOPLE_DEFAULT_ORDERING } from '../../../constants/people';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { sticky } from '../../../constants/helperCssRules';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { jobTitlesSelector } from '../../../store/selectors/jobTitlesSelector';
import { levelsAndTracksSelector } from '../../../store/selectors/levelsAndTracksSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import { absenceSetsSelector } from '../../../store/selectors/absenceSetsSelector';
import {
  getAllAbsenceSets,
  clearAllAbsenceSets,
} from '../../../store/modules/absenceSets';
import { clearJobTitles, getJobTitles } from '../../../store/modules/jobTitles';
import {
  clearTagsCategories,
  getTagsCategories,
} from '../../../store/modules/tags';
import {
  clearLevelsAndTracks,
  getLevelsAndTracks,
} from '../../../store/modules/levelsAndTracks';
import {
  clearDivisionsAndDepartments,
  getDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import http from '../../../utility/http';
import {
  isUserProfileAccessibleCheck,
  hasAuthPermissionCheck,
} from '../../../utility/permissions';
import { getObjectToNumberArray, isArrayEmpty } from '../../../utility/helpers';
import { useTranslations } from '../../../utility/useTranslations';
import { showSuccessMessage } from '../../../utility/uiUtils';
import {
  prepareAbsenceSetsData,
  prepareAccrualOptions,
} from '../../../utility/absence';
import { formatDate } from '../../../utility/dateUtils';
import { preparePeopleFilters } from '../../../utility/people';
import { prepareRoles } from '../../../utility/roles';
import {
  DUPLICATE_SET_FIELD,
  prepareAbsence,
  prepareAbsences,
  prepareHolidayInitialData,
  prepareManageHolidayFields,
  prepareAbsenceSetInitialData,
  prepareManageAbsenceSetFields,
  prepareRemoveUsersInitialData,
  prepareManageAbsenceFields,
  prepareManageAbsenceInitialData,
  prepareManageAbsenceTemplateColumns,
  prepareManageAbsenceTemplateInitialData,
  prepareManageExtensionInitialData,
  prepareManageExtensionFields,
  prepareAssignedUsers,
  prepareUsersField,
  prepareAddUsersInitialData,
  prepareAddUsersField,
  prepareAddUserList,
  prepareAddAbsenceOrExtensionUsersInitialData,
  filterUsers,
} from './config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      width: '100%',
      maxWidth: '100%',
    },
    header: {
      ...sticky(primary.white, 105),
      padding: spacing(8, 0, 6, 0),
    },
    description: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    numberOfDaysField: {
      width: 75,
    },
    drawerContent: {
      height: '100%',
    },
    drawerForm: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
    addPeopleForm: {
      position: 'relative',
      justifyContent: 'space-between',
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      marginBottom: 0,
      minHeight: 0,
    },
    picker: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
    },
    types: {
      display: 'grid',
      gridColumnGap: 16,
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
    checkboxWrapperClass: {
      marginBottom: 0,
      height: 140,
      position: 'absolute',
      bottom: 0,
      width: '100%',
      zIndex: 99,
    },
    colorPicker: {
      width: 'fit-content',
    },
    absenceForm: {
      display: 'grid',
      gridColumnGap: 24,
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gridTemplateAreas: `
      "name name"
      "icon color"
      "hasAvailableDays hasAvailableDays"
      "availableDays availableDays"
      "accrual accrual"
      "hasRollover hasRollover"
      "rollover rollover"
      "hasExtensions hasExtensions"
      "hasTemplate hasTemplate"
      "hasReasons hasReasons"
      "reasons reasons"
    `,
    },
    name: {
      gridArea: 'name',
    },
    icon: {
      gridArea: 'icon',
    },
    color: {
      gridArea: 'color',
    },
    hasAvailableDays: {
      gridArea: 'hasAvailableDays',
    },
    availableDays: {
      gridArea: 'availableDays',
    },
    accrual: {
      gridArea: 'accrual',
    },
    hasRollover: {
      gridArea: 'hasRollover',
    },
    rollover: {
      gridArea: 'rollover',
    },
    hasExtensions: {
      gridArea: 'hasExtensions',
    },
    hasTemplate: {
      gridArea: 'hasTemplate',
    },
    hasReasons: {
      gridArea: 'hasReasons',
    },
    reasons: {
      gridArea: 'reasons',
    },
    absenceTemplateRichText: {
      height: 'calc(100vh - 280px)',
      minHeight: 340,
      '& .ql-toolbar.ql-snow': {
        flexWrap: 'wrap',
      },
      '& .ql-editor': {
        minHeight: 240,
      },
      '& .ql-container': {
        height: 400,
      },
    },
    absenceTemplateColumns: {
      display: 'grid',
      flex: '1 1 auto',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      minHeight: 0,
      width: '100%',
      '& > *': {
        minWidth: 0,
      },
    },
    absenceTemplateEditorColumn: {
      flexDirection: 'column',
      padding: spacing(4, 5),
    },
    absenceTemplateDescription: {
      color: primary.bluish1,
      lineHeight: '20px',
      marginTop: spacing(3),
      marginBottom: spacing(3),
      whiteSpace: 'pre-wrap',
      '& > *': {
        fontWeight: 'normal',
      },
    },
    templateVariablesGrid: {
      display: 'grid',
      gap: spacing(2, 3),
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      [breakpoints.down('md')]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
      [breakpoints.down('xs')]: {
        gridTemplateColumns: 'minmax(0, 1fr)',
      },
    },
    templateVariablesColumn: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    templateVariablesTitle: {
      fontWeight: 600,
      marginBottom: spacing(1),
    },
    templateVariablesValue: {
      display: 'block',
      marginBottom: spacing(0.5),
      wordBreak: 'break-word',
    },
    absenceTemplatePreviewColumn: {
      padding: 0,
      height: '100%',
      flexDirection: 'column',
    },
    absenceTemplatePreviewColumnInner: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
    },
    absenceTemplatePreviewTitle: {
      marginBottom: spacing(2),
      padding: spacing(4, 5, 0),
    },
    absenceTemplatePreviewDescription: {
      color: primary.bluish1,
      lineHeight: '20px',
      marginBottom: spacing(4),
      padding: spacing(0, 5),
      '& > *': {
        fontWeight: 'normal',
      },
    },
    absenceTemplatePreviewField: {
      flex: '1 1 auto',
      minHeight: 0,
      margin: 0,
    },
  })
);

const AbsenceSettingsPage = ({
  navigate,
  auth,
  accessControl,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.ABSCENCE_MANAGEMENT);
  const { USER_COUNT } = ABSENCE_FIELD_NAMES;
  const currentYear = +formatDate(new Date(), 'yyyy');

  const { jobTitles } = useSelector(jobTitlesSelector);
  const { tracks } = useSelector(levelsAndTracksSelector);
  const { divisions, departments } = useSelector(
    divisionsAndDepartmentsSelector
  );
  const { categories } = useSelector(tagsSelector);

  const absenceSets = useSelector(absenceSetsSelector);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isManageHolidayOpened, setIsManageHolidayOpened] = useState(false);
  const [holiday, setHoliday] = useState(null);
  const [isDeleteHolidayOpened, setIsDeleteHolidayOpened] = useState(false);
  const [isManageSetOpened, setIsManageSetOpened] = useState(false);
  const [isDuplicateSetOpened, setIsDuplicateSetOpened] = useState(false);
  const [isDeleteSetOpened, setIsDeleteSetOpened] = useState(false);
  const [isDeleteSetWarningOpened, setIsDeleteSetWarningOpened] =
    useState(false);
  const [isRemoveSetUsersOpened, setIsRemoveSetUsersOpened] = useState(false);
  const [absenceSet, setAbsenceSet] = useState(null);
  const [absenceSetUsers, setAbsenceSetUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isManageAbsenceOpened, setIsManageAbsenceOpened] = useState(false);
  const [isManageAbsenceTemplateOpened, setIsManageAbsenceTemplateOpened] =
    useState(false);
  const [absence, setAbsence] = useState(null);
  const [absenceTemplate, setAbsenceTemplate] = useState(null);
  const [isDeleteAbsenceOpened, setIsDeleteAbsenceOpened] = useState(false);
  const [isDeleteAbsenceWarningOpened, setIsDeleteAbsenceWarningOpened] =
    useState(false);
  const [isRemoveAbsenceUsersOpened, setIsRemoveAbsenceUsersOpened] =
    useState(false);
  const [isAddSetUsersOpened, setIsAddSetUsersOpened] = useState(false);
  const [isAddAbsenceUsersOpened, setIsAddAbsenceUsersOpened] = useState(false);
  const [isAddExtensionUsersOpened, setIsAddExtensionUsersOpened] =
    useState(false);
  const [absenceUsers, setAbsenceUsers] = useState([]);
  const [isManageExtensionOpened, setIsManageExtensionOpened] = useState(false);
  const [extension, setExtension] = useState(null);
  const [isDeleteExtensionOpened, setIsDeleteExtensionOpened] = useState(false);
  const [isDeleteExtensionWarningOpened, setIsDeleteExtensionWarningOpened] =
    useState(false);
  const [isRemoveExtensionUsersOpened, setIsRemoveExtensionUsersOpened] =
    useState(false);
  const [extensionUsers, setExtensionUsers] = useState([]);

  const systemRoles = prepareRoles(
    translations.systemRoles,
    SYSTEM_ROLE_OPTIONS,
    true
  );
  const canViewAbsenceReport = hasAuthPermissionCheck(
    PERMISSION_CODES.REPORTS_OVERVIEW_VIEW,
    accessControl
  );
  const accrualOptions = prepareAccrualOptions(translations.accrualOptions);
  const formatedSets = prepareAbsenceSetsData(
    translations,
    absenceSets,
    accrualOptions
  );
  const manageHolidayInitialData = prepareHolidayInitialData(holiday);
  const userField = prepareUsersField(classes, translations);
  const manageAbsenceSetInitialData = prepareAbsenceSetInitialData(absenceSet);
  const manageAbsenceSetFields = prepareManageAbsenceSetFields(!!absenceSet);
  const manageHolidayFields = prepareManageHolidayFields(
    classes,
    currentYear,
    absenceSet?.id,
    holiday?.id
  );
  const removeSetUsersInitialData =
    prepareRemoveUsersInitialData(absenceSetUsers);
  const manageAbsenceInitialData = prepareManageAbsenceInitialData(absence);
  const manageAbsenceFields = prepareManageAbsenceFields(classes);
  const manageAbsenceTemplateInitialData =
    prepareManageAbsenceTemplateInitialData(absenceTemplate);
  const manageAbsenceTemplateColumns = prepareManageAbsenceTemplateColumns(
    classes,
    translations.manageAbsenceTemplate.preview,
    translations.manageAbsenceTemplate.formLabels.templateVariables.groups
  );
  const removeAbsenceUsersInitialData =
    prepareRemoveUsersInitialData(absenceUsers);
  const addSetUsersInitialData = prepareAddUsersInitialData(
    allUsers,
    absenceSet?.types
  );
  const addSetUsersFields = prepareAddUsersField(
    classes,
    absenceSet,
    allUsers,
    absenceSetUsers,
    preparePeopleFilters(
      translations,
      jobTitles,
      tracks,
      divisions,
      departments,
      systemRoles,
      allUsers,
      categories
    ),
    !isArrayEmpty(absenceSet?.types)
  );
  const addAbsenceUsersInitialData =
    prepareAddAbsenceOrExtensionUsersInitialData(absenceSetUsers);
  const addAbsenceUsersFields = prepareAddUsersField(
    classes,
    absenceSet,
    absenceSetUsers,
    absenceUsers,
    preparePeopleFilters(
      translations,
      jobTitles,
      tracks,
      divisions,
      departments,
      systemRoles,
      absenceSetUsers,
      categories
    )
  );
  const manageExtensionInitialData =
    prepareManageExtensionInitialData(extension);
  const manageExtensionFields = prepareManageExtensionFields(classes);
  const removeExtensionUsersInitialData =
    prepareRemoveUsersInitialData(extensionUsers);
  const addExtensionUsersInitialData =
    prepareAddAbsenceOrExtensionUsersInitialData(absenceUsers);
  const addExtensionUsersFields = prepareAddUsersField(
    classes,
    absenceSet,
    absenceUsers,
    extensionUsers,
    preparePeopleFilters(
      translations,
      jobTitles,
      tracks,
      divisions,
      departments,
      systemRoles,
      absenceUsers,
      categories
    )
  );

  const canAccessUserProfileCheck = useCallback(
    userId => isUserProfileAccessibleCheck(accessControl, auth.id, userId),
    [accessControl, auth.id]
  );

  const onGoToUserProfile = userId => navigate(`/people/${userId}/absences`);

  const handleGoToAbsenceReport = set => absenceType => () => {
    navigate('/reports/absence', {
      state: {
        absenceSetId: set.id,
        absenceTypeId: absenceType.id,
      },
    });
  };

  const getAbsenceSets = useCallback(async () => {
    try {
      await Promise.all([
        getAllAbsenceSets(dispatch),
        getJobTitles(dispatch),
        getTagsCategories(dispatch),
        getLevelsAndTracks(dispatch),
        getDivisionsAndDepartments(dispatch),
      ]);
    } finally {
      setIsInitialLoad(false);
    }
  }, [dispatch]);

  const handleOpenManageSet = () => setIsManageSetOpened(true);

  const handleCloseManageSet = () => {
    setIsManageSetOpened(false);
    setAbsenceSet(null);
  };

  const handleOpenEditSet = set => {
    setAbsenceSet(set);
    handleOpenManageSet();
  };

  const onSaveSet = async ({ name, types }) => {
    const preparedTypes = prepareAbsences(types);

    if (absenceSet) {
      await http.patch(api_absence_set(absenceSet.id), {
        name,
        types: preparedTypes,
      });
      showSuccessMessage(translations.successMessages.editSet);
      return getAllAbsenceSets(dispatch);
    }

    await http.post(API_ABSENCE_SETS, { name, types: preparedTypes });
    showSuccessMessage(translations.successMessages.addSet);
    return getAllAbsenceSets(dispatch);
  };

  const handleGetSetUsers = setId => async () => {
    const assignedSetUsers = await http.get(api_absence_set_users(setId));
    setAbsenceSetUsers(assignedSetUsers?.data || []);
    return assignedSetUsers;
  };

  const handleOpenAddSetUsers = set => async () => {
    const allAvailableUsers = await http.get(API_USERS_BASIC, {
      params: {
        ...PEOPLE_DEFAULT_ORDERING,
        [PARAMS.EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
        [PARAMS.EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
        [PARAMS.ABSENCE_SET]: set.id,
      },
    });

    setAllUsers(
      prepareAddUserList(
        allAvailableUsers.data,
        set[USER_COUNT] !== 0 ? absenceSetUsers : []
      )
    );
    setIsAddSetUsersOpened(true);
    setAbsenceSet(set);
  };

  const handleCloseAddSetUsers = () => {
    setIsAddSetUsersOpened(false);
    setAbsenceSet(null);
    setAllUsers([]);
  };

  const onAddSetUsers = async values => {
    const params = {
      absences: values.types,
      users: getObjectToNumberArray(values.users.filteredValues),
    };

    await http
      .post(api_absence_set_and_absence_add_users(absenceSet.id), params)
      .then(() => {
        getAllAbsenceSets(dispatch);
        showSuccessMessage(
          translations.assignUsersToSetAndAbsences.addSetUsersSuccess
        );
      });
  };

  const handleRemoveSetUsers = set => async () => {
    setAbsenceSetUsers(prepareAssignedUsers(absenceSetUsers));
    setAbsenceSet(set);
    setIsRemoveSetUsersOpened(true);
  };

  const handleCloseRemoveSetUsers = () => {
    setAllUsers([]);
    setAbsenceSetUsers([]);
    setAbsenceSet(null);
    setIsRemoveSetUsersOpened(false);
  };

  const onRemoveSetUsers = async ({ users }) => {
    await http.post(api_absence_set_users(absenceSet.id), {
      users: filterUsers(users),
    });
    showSuccessMessage(translations.successMessages.addSetUsers);
    return getAllAbsenceSets(dispatch);
  };

  const handleOpenDuplicateSet = set => {
    setAbsenceSet(set);
    setIsDuplicateSetOpened(true);
  };

  const handleCloseDuplicateSet = () => {
    setAbsenceSet(null);
    setIsDuplicateSetOpened(false);
  };

  const onSaveDuplicateSet = async ({ name }) => {
    await http.post(api_absence_set_duplicate(absenceSet.id), { name });
    showSuccessMessage(translations.successMessages.duplicateSet);
    return getAllAbsenceSets(dispatch);
  };

  const handleOpenDeleteSet = set => {
    setAbsenceSet(set);

    if (set?.[USER_COUNT] !== 0) {
      return setIsDeleteSetWarningOpened(true);
    }
    setIsDeleteSetOpened(true);
  };

  const handleCloseDeleteSetDialog = () => {
    setIsDeleteSetOpened(false);
  };

  const handleCloseDeleteSetDialogWarning = () => {
    setIsDeleteSetWarningOpened(false);
  };

  const onDeleteSet = async () => {
    try {
      await http.delete(api_absence_set(absenceSet.id));
      showSuccessMessage(translations.deleteSetDialog.deleteSuccess);
      getAllAbsenceSets(dispatch);
    } finally {
      setIsDeleteSetOpened(false);
      setAbsenceSet(null);
      setIsManageSetOpened(false);
    }
  };

  const handleOpenManageHoliday = set => e => {
    e.stopPropagation();
    setAbsenceSet(set);
    setIsManageHolidayOpened(true);
  };

  const handleOpenEditHoliday = currentHoliday => {
    const { set, ...currHoliday } = currentHoliday;
    setHoliday(currHoliday);
    setAbsenceSet(set);
    setIsManageHolidayOpened(true);
  };

  const handleCloseManageHoliday = () => {
    setIsManageHolidayOpened(false);
    setAbsenceSet(null);
    setHoliday(null);
  };

  const onSaveHoliday = async ({ name, dates }) => {
    const data = {
      ...(!holiday ? { absence_set: absenceSet.id } : {}),
      name,
      dates,
    };
    if (holiday) {
      await http.patch(api_holiday(holiday.id), data);
      showSuccessMessage(translations.successMessages.editHoliday);
      return getAllAbsenceSets(dispatch);
    }

    await http.post(API_HOLIDAYS, data);
    showSuccessMessage(translations.successMessages.addHoliday);
    return getAllAbsenceSets(dispatch);
  };

  const handleOpenDeleteHoliday = currentHoliday => {
    setHoliday(currentHoliday);
    setIsDeleteHolidayOpened(true);
  };

  const handleCloseDeleteHolidayDialog = () => {
    setIsDeleteHolidayOpened(false);
  };

  const onDeleteHoliday = async () => {
    try {
      await http.delete(api_holiday(holiday.id));
      showSuccessMessage(translations.deleteHolidayDialog.deleteSuccess);
      getAllAbsenceSets(dispatch);
    } finally {
      setIsDeleteHolidayOpened(false);
      setHoliday(null);
      setIsManageHolidayOpened(false);
    }
  };

  const handleOpenManageAbsence =
    set =>
    (currentAbsence = null) =>
    e => {
      e.stopPropagation();
      setIsManageAbsenceOpened(true);
      setAbsenceSet(set);
      setAbsence(currentAbsence);
    };

  const handleOpenEditAbsence = set => currentAbsence => {
    setIsManageAbsenceOpened(true);
    setAbsenceSet(set);
    setAbsence(currentAbsence);
  };

  const handleOpenEditAbsenceTemplate = async currentAbsence => {
    const { data } = await http.get(
      api_absence_report_template(currentAbsence.id)
    );

    setAbsenceTemplate({
      id: data?.absence,
      absenceId: currentAbsence.id,
      template: data.template,
    });
    setIsManageAbsenceTemplateOpened(true);
  };

  const handleCloseManageAbsenceTemplate = () => {
    setIsManageAbsenceTemplateOpened(false);
    setAbsenceTemplate(null);
  };

  const onSaveAbsenceTemplate = async values => {
    const { isEditTemplate, id, template } = values;
    const data = { template };
    if (isEditTemplate) {
      await http.patch(api_absence_report_template(id), data);
      showSuccessMessage(translations.successMessages.editTemplate);
    } else {
      await http.post(api_absence_report_template(id), data);
      showSuccessMessage(translations.successMessages.addTemplate);
    }
    await getAllAbsenceSets(dispatch);
    handleCloseManageAbsenceTemplate();
  };

  const handleCloseManageAbsence = () => {
    setIsManageAbsenceOpened(false);
    setAbsenceSet(null);
    setAbsence(null);
  };

  const onSaveAbsence = async values => {
    const data = prepareAbsence(values, absenceSet);

    if (absence) {
      await http.patch(api_absence(absence.id), data);
      showSuccessMessage(translations.successMessages.editAbsence);
      return getAllAbsenceSets(dispatch);
    }
    handleCloseManageAbsence();
    await http.post(API_ABSENCE, data);
    showSuccessMessage(translations.successMessages.addAbsence);
    return getAllAbsenceSets(dispatch);
  };

  const handleOpenDeleteAbsence = currentAbsence => {
    setAbsence(currentAbsence);

    if (currentAbsence[USER_COUNT] !== 0) {
      return setIsDeleteAbsenceWarningOpened(true);
    }

    setIsDeleteAbsenceOpened(true);
  };

  const handleCloseDeleteAbsenceDialog = () => {
    setIsDeleteAbsenceOpened(false);
  };

  const handleCloseDeleteAbsenceWarningDialog = () => {
    setIsDeleteAbsenceWarningOpened(false);
  };

  const onDeleteAbsence = async () => {
    try {
      await http.delete(api_absence(absence.id));
      showSuccessMessage(translations.deleteAbsenceDialog.deleteSuccess);
      getAllAbsenceSets(dispatch);
    } finally {
      setIsDeleteAbsenceOpened(false);
      setAbsence(null);
      setIsManageAbsenceOpened(false);
    }
  };

  const handleGetAbsenceUsers = absenceId => async () => {
    const currentAbsenceUsers = await http.get(api_absence_users(absenceId));
    setAbsenceUsers(prepareAssignedUsers(currentAbsenceUsers?.data || []));
    return currentAbsenceUsers;
  };

  const handleOpenAddAbsenceUsers = set => currentAbsence => async () => {
    const setUsers = await handleGetSetUsers(set.id)();

    setAbsenceSetUsers(
      prepareAddUserList(
        setUsers.data,
        currentAbsence[USER_COUNT] !== 0 ? absenceUsers : []
      )
    );
    if (currentAbsence[USER_COUNT] === 0) {
      setAbsenceUsers([]);
    }
    setAbsence(currentAbsence);
    setIsAddAbsenceUsersOpened(true);
  };

  const handleCloseAddAbsenceUsers = () => {
    setIsAddAbsenceUsersOpened(false);
    setAbsence(null);
  };

  const onAddAbsenceUsers = async values => {
    const params = {
      users: [
        ...getObjectToNumberArray(absenceUsers),
        ...getObjectToNumberArray(values.users.filteredValues),
      ],
    };

    await http.post(api_absence_users(absence.id), params).then(() => {
      getAllAbsenceSets(dispatch);
      showSuccessMessage(
        translations.assignUsersToSetAndAbsences.addUsersToAbsenceSuccess
      );
    });
  };

  const handleRemoveAbsenceUsers = () => currentAbsence => async () => {
    setAbsence(currentAbsence);
    setIsRemoveAbsenceUsersOpened(true);
  };

  const handleCloseRemoveAbsenceUsers = () => {
    setAbsenceSetUsers([]);
    setAbsenceUsers([]);
    setAbsence(null);
    setIsRemoveAbsenceUsersOpened(false);
  };

  const onRemoveAbsenceUsers = async ({ users }) => {
    await http.post(api_absence_users(absence.id), {
      users: filterUsers(users),
    });
    showSuccessMessage(translations.successMessages.updateAbsenceUsers);
    return getAllAbsenceSets(dispatch);
  };

  const handleOpenManageExtension =
    currentAbsence =>
    (currentExtension = null) =>
    e => {
      e.stopPropagation();
      setIsManageExtensionOpened(true);
      setAbsence(currentAbsence);
      setExtension(currentExtension);
    };

  const handleCloseManageExtension = () => {
    setIsManageExtensionOpened(false);
    setAbsence(null);
    setExtension(null);
  };

  const handleOpenEditExtension = currentExtension => {
    setIsManageExtensionOpened(true);
    setExtension(currentExtension);
  };

  const onSaveExtension = async ({ name, description, number_of_days }) => {
    const data = {
      name,
      description,
      number_of_days: +number_of_days,
      ...(!extension ? { absence: absence.id } : {}),
    };

    if (extension) {
      await http.patch(api_absence_extension(extension.id), data);
      showSuccessMessage(translations.successMessages.editExtension);
      return getAllAbsenceSets(dispatch);
    }

    await http.post(API_ABSENCE_EXTENSIONS, data);
    showSuccessMessage(translations.successMessages.addExtension);
    return getAllAbsenceSets(dispatch);
  };

  const handleOpenDeleteExtension = currentExtension => {
    setExtension(currentExtension);

    if (currentExtension[USER_COUNT] !== 0) {
      return setIsDeleteExtensionWarningOpened(true);
    }

    setIsDeleteExtensionOpened(true);
  };

  const handleCloseDeleteExtensionDialog = () => {
    setIsDeleteExtensionOpened(false);
  };

  const handleCloseDeleteExtensionWarningDialog = () => {
    setIsDeleteExtensionWarningOpened(false);
  };

  const onDeleteExtension = async () => {
    try {
      await http.delete(api_absence_extension(extension.id));
      showSuccessMessage(translations.deleteExtensionDialog.deleteSuccess);
      getAllAbsenceSets(dispatch);
    } finally {
      setIsDeleteExtensionOpened(false);
      setExtension(null);
      setIsManageExtensionOpened(false);
    }
  };

  const handleGetExtensionUsers = extensionId => async () => {
    const extensionAssignedUsers = await http.get(
      api_absence_extension_users(extensionId)
    );
    setExtensionUsers(extensionAssignedUsers?.data || []);
    return extensionAssignedUsers;
  };

  const handleOpenAddExtensionUsers =
    absenceId => currentExtension => async () => {
      const currentAbsenceUsers = await handleGetAbsenceUsers(absenceId)();
      setAbsenceUsers(
        prepareAddUserList(
          currentAbsenceUsers.data,
          currentExtension[USER_COUNT] !== 0 ? extensionUsers : []
        )
      );
      if (currentExtension[USER_COUNT] === 0) {
        setExtensionUsers([]);
      }
      setExtension(currentExtension);
      setIsAddExtensionUsersOpened(true);
    };

  const handleCloseAddExtensionUsers = () => {
    setIsAddExtensionUsersOpened(false);
    setExtension(null);
    setAbsenceUsers([]);
  };

  const onAddExtenisonUsers = async values => {
    const params = {
      users: [
        ...getObjectToNumberArray(extensionUsers),
        ...getObjectToNumberArray(values.users.filteredValues),
      ],
    };

    await http
      .post(api_absence_extension_users(extension.id), params)
      .then(() => {
        getAllAbsenceSets(dispatch);
        showSuccessMessage(
          translations.assignUsersToSetAndAbsences.addUsersToExtensionSuccess
        );
      });
  };

  const handleRemoveExtensionUsers = () => currentExtension => async () => {
    setExtension(currentExtension);
    setIsRemoveExtensionUsersOpened(true);
  };

  const handleCloseRemoveExtensionUsers = () => {
    setAbsenceUsers([]);
    setExtensionUsers([]);
    setExtension(null);
    setIsRemoveExtensionUsersOpened(false);
  };

  const onRemoveExtensionUsers = async ({ users }) => {
    await http.post(api_absence_extension_users(extension.id), {
      users: filterUsers(users),
    });
    showSuccessMessage(translations.successMessages.addExtensionUsers);
    return getAllAbsenceSets(dispatch);
  };

  const cleanup = useCallback(() => {
    dispatch(clearAllAbsenceSets());
    dispatch(clearJobTitles());
    dispatch(clearTagsCategories());
    dispatch(clearLevelsAndTracks());
    dispatch(clearDivisionsAndDepartments());
  }, [dispatch]);

  useEffect(() => {
    getAbsenceSets();
  }, [getAbsenceSets]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return (
    <PageContainer
      {...rest}
      translations={translations}
      navigate={navigate}
      auth={auth}
      accessControl={accessControl}
      dispatch={dispatch}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoad && (
        <div className={classes.root}>
          <div className={classes.header}>
            <div className={classes.description}>
              <Typography variant="body2">
                {translations.description}
              </Typography>
              <CustomButton
                className={classes.addAttribute}
                type="addRoundedNew"
                onClick={handleOpenManageSet}
              >
                {translations.addSetButton}
              </CustomButton>
            </div>
          </div>
          <AbsenceSets
            translations={translations.absenceSets}
            sets={formatedSets}
            canAccessUserProfileCheck={canAccessUserProfileCheck}
            canViewAbsenceReport={canViewAbsenceReport}
            onGoToProfilePage={onGoToUserProfile}
            onGoToAbsenceReport={handleGoToAbsenceReport}
            onEditSet={handleOpenEditSet}
            onDuplicateSet={handleOpenDuplicateSet}
            onDeleteSet={handleOpenDeleteSet}
            onGetSetUsers={handleGetSetUsers}
            onRemoveSetUsers={handleRemoveSetUsers}
            onAddAbsence={handleOpenManageAbsence}
            onEditAbsence={handleOpenEditAbsence}
            onEditAbsenceTemplate={handleOpenEditAbsenceTemplate}
            onDeleteAbsence={handleOpenDeleteAbsence}
            onGetAbsenceUsers={handleGetAbsenceUsers}
            onRemoveAbsenceUsers={handleRemoveAbsenceUsers}
            onAddExtension={handleOpenManageExtension}
            onEditExtension={handleOpenEditExtension}
            onDeleteExtension={handleOpenDeleteExtension}
            onGetExtensionUsers={handleGetExtensionUsers}
            onRemoveExtensionUsers={handleRemoveExtensionUsers}
            onAddHoliday={handleOpenManageHoliday}
            onEditHoliday={handleOpenEditHoliday}
            onDeleteHoliday={handleOpenDeleteHoliday}
            onAddSetUsers={handleOpenAddSetUsers}
            onAddAbsenceUsers={handleOpenAddAbsenceUsers}
            onAddExtensionUsers={handleOpenAddExtensionUsers}
          />
          <CustomFormDrawer
            translations={translations.manageAbsenceSet}
            isOpened={isManageSetOpened}
            initialData={manageAbsenceSetInitialData}
            isInitialValid={!!absenceSet}
            fields={manageAbsenceSetFields}
            absenceIcons={ABSENCE_ICONS}
            accrualOptions={accrualOptions}
            onDelete={handleOpenDeleteSet}
            onClose={handleCloseManageSet}
            onSave={onSaveSet}
            hasCancelButton
          />
          <CustomFormDrawer
            customContentClass={classes.drawerContent}
            customFormClass={classNames(
              classes.drawerForm,
              classes.addPeopleForm
            )}
            translations={translations.assignUsersToSetAndAbsences}
            isOpened={isAddSetUsersOpened}
            initialData={addSetUsersInitialData}
            fields={addSetUsersFields}
            onClose={handleCloseAddSetUsers}
            onSave={onAddSetUsers}
            hasCancelButton
            isMiddleWidth
            hideDelete
          />
          <CustomFormDrawer
            customContentClass={classes.drawerContent}
            translations={translations.assignSetUsers}
            isOpened={isRemoveSetUsersOpened}
            initialData={removeSetUsersInitialData}
            fields={userField}
            onClose={handleCloseRemoveSetUsers}
            onSave={onRemoveSetUsers}
            hasCancelButton
            hideDelete
            isInitialValid
          />
          <CustomFormDrawer
            translations={translations.duplicateAbsenceSet}
            isOpened={isDuplicateSetOpened}
            initialData={manageAbsenceSetInitialData}
            fields={DUPLICATE_SET_FIELD}
            onClose={handleCloseDuplicateSet}
            onSave={onSaveDuplicateSet}
            hideDelete
            hasCancelButton
          />
          <CustomFormDrawer
            translations={translations.manageHoliday}
            isOpened={isManageHolidayOpened}
            initialData={manageHolidayInitialData}
            isInitialValid={!!holiday}
            fields={manageHolidayFields}
            onDelete={handleOpenDeleteHoliday}
            onClose={handleCloseManageHoliday}
            onSave={onSaveHoliday}
            hasCancelButton
          />
          <CustomFormDrawer
            translations={translations.manageAbsence}
            customFormClass={classes.absenceForm}
            isOpened={isManageAbsenceOpened}
            initialData={manageAbsenceInitialData}
            isInitialValid={!!absence}
            fields={manageAbsenceFields}
            absenceIcons={ABSENCE_ICONS}
            accrualOptions={accrualOptions}
            onDelete={handleOpenDeleteAbsence}
            onClose={handleCloseManageAbsence}
            onSave={onSaveAbsence}
            hasCancelButton
          />
          <CustomFormDrawer
            translations={translations.manageAbsenceTemplate}
            isOpened={isManageAbsenceTemplateOpened}
            initialData={manageAbsenceTemplateInitialData}
            isInitialValid
            columns={manageAbsenceTemplateColumns}
            customColumnsClass={classes.absenceTemplateColumns}
            onClose={handleCloseManageAbsenceTemplate}
            onSave={onSaveAbsenceTemplate}
            hasCancelButton
            hideDelete
            isWholeScreen
          />
          <CustomFormDrawer
            customContentClass={classes.drawerContent}
            customFormClass={classNames(
              classes.drawerForm,
              classes.addPeopleForm
            )}
            translations={translations.assignUsersToSetAndAbsences}
            isOpened={isAddAbsenceUsersOpened}
            initialData={addAbsenceUsersInitialData}
            fields={addAbsenceUsersFields}
            onClose={handleCloseAddAbsenceUsers}
            onSave={onAddAbsenceUsers}
            hasCancelButton
            isMiddleWidth
            hideDelete
          />
          <CustomFormDrawer
            customContentClass={classes.drawerContent}
            translations={translations.assignAbsenceUsers}
            isOpened={isRemoveAbsenceUsersOpened}
            initialData={removeAbsenceUsersInitialData}
            fields={userField}
            onClose={handleCloseRemoveAbsenceUsers}
            onSave={onRemoveAbsenceUsers}
            hasCancelButton
            hideDelete
            isInitialValid
          />
          <CustomFormDrawer
            translations={translations.manageExtension}
            isOpened={isManageExtensionOpened}
            initialData={manageExtensionInitialData}
            isInitialValid={!!extension}
            fields={manageExtensionFields}
            onDelete={handleOpenDeleteExtension}
            onClose={handleCloseManageExtension}
            onSave={onSaveExtension}
            hasCancelButton
          />
          <CustomFormDrawer
            customContentClass={classes.drawerContent}
            customFormClass={classNames(
              classes.drawerForm,
              classes.addPeopleForm
            )}
            translations={translations.assignUsersToSetAndAbsences}
            isOpened={isAddExtensionUsersOpened}
            initialData={addExtensionUsersInitialData}
            fields={addExtensionUsersFields}
            onClose={handleCloseAddExtensionUsers}
            onSave={onAddExtenisonUsers}
            hasCancelButton
            isMiddleWidth
            hideDelete
          />
          <CustomFormDrawer
            customContentClass={classes.drawerContent}
            customFormClass={classes.drawerForm}
            translations={translations.assignExtensionUsers}
            isOpened={isRemoveExtensionUsersOpened}
            initialData={removeExtensionUsersInitialData}
            fields={userField}
            onClose={handleCloseRemoveExtensionUsers}
            onSave={onRemoveExtensionUsers}
            hasCancelButton
            hideDelete
            isInitialValid
          />
          <AlertDialog
            translations={translations.deleteSetDialog}
            isOpened={isDeleteSetOpened}
            onClose={handleCloseDeleteSetDialog}
            onConfirm={onDeleteSet}
            isWarning
          />
          <AlertDialog
            translations={translations.deleteSetDialogWarning}
            isOpened={isDeleteSetWarningOpened}
            onClose={handleCloseDeleteSetDialogWarning}
            isWarning
          />
          <AlertDialog
            translations={translations.deleteHolidayDialog}
            isOpened={isDeleteHolidayOpened}
            onClose={handleCloseDeleteHolidayDialog}
            onConfirm={onDeleteHoliday}
            isWarning
          />
          <AlertDialog
            translations={translations.deleteAbsenceDialog}
            isOpened={isDeleteAbsenceOpened}
            onClose={handleCloseDeleteAbsenceDialog}
            onConfirm={onDeleteAbsence}
            isWarning
          />
          <AlertDialog
            translations={translations.deleteAbsenceWarningDialog}
            isOpened={isDeleteAbsenceWarningOpened}
            onClose={handleCloseDeleteAbsenceWarningDialog}
            isWarning
          />
          <AlertDialog
            translations={translations.deleteExtensionDialog}
            isOpened={isDeleteExtensionOpened}
            onClose={handleCloseDeleteExtensionDialog}
            onConfirm={onDeleteExtension}
            isWarning
          />
          <AlertDialog
            translations={translations.deleteExtensionWarningDialog}
            isOpened={isDeleteExtensionWarningOpened}
            onClose={handleCloseDeleteExtensionWarningDialog}
            isWarning
          />
          <NotificationCard
            title={translations.noResults.title}
            content={translations.noResults.content}
            shouldFade={!isInitialLoad && isArrayEmpty(absenceSets)}
          />
        </div>
      )}
    </PageContainer>
  );
};

AbsenceSettingsPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(AbsenceSettingsPage);
